var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSafeFormData, } from '../types/index';
import { useEventDateTime } from '../hooks/useDateTime';
import { useHandleEventCreation } from '../hooks/useHandleEventCreation';
import { useCalendarIntegrationsContext } from '../context/CalendarIntegrationContext';
import { useEventComposerGlobalContext } from './EventComposerGlobalContext';
import { useConfig } from '../context/ConfigContext';
import { useEventApi } from '../hooks/useEvent/useEventApi';
import { useFetchInitialSpaceForComposer } from '../components/SpaceDetails/hooks/useFetchInitialSpaceForComposer';
import { useSpacePrivacySettings } from '../hooks/useSpacePrivacySettings';
var CreateEventContext = createContext({
    initialEvent: undefined,
    handleEventCreation: function () { },
    fetchingSpace: false,
    createEventError: null,
    creatingEvent: false,
    setCreateEventError: function () { },
    completedSpaceRequestFormId: undefined,
    setCompletedSpaceRequestFormId: function () { },
    shouldShowPrivacyIndicator: false,
    spaceReservationVisibilityEnabled: false,
});
export var CreateEventContextProvider = function (_a) {
    var _b, _c;
    var children = _a.children, onClose = _a.onClose, onSubmit = _a.onSubmit, onEventChange = _a.onEventChange, initialEvent = _a.initialEvent, parentEvent = _a.parentEvent;
    var _d = useEventComposerGlobalContext(), timezone = _d.timezone, requestOnlySpace = _d.requestOnlySpace, onTrackBufferEvent = _d.onTrackBufferEvent, onError = _d.onError;
    var _e = useConfig(), tenantId = _e.tenantId, environmentUrls = _e.environmentUrls, userContext = _e.userContext, accessToken = _e.accessToken;
    var apiUrl = environmentUrls.apiUrl, baseApiUrl = environmentUrls.baseApiUrl;
    var currentOrg = userContext.currentOrg;
    var subscribe = useFormContext().subscribe;
    var formData = useSafeFormData();
    var _f = useEventApi({
        onError: onError,
        apiUrl: apiUrl,
        baseApiUrl: baseApiUrl,
        currentOrg: currentOrg,
        tenantId: tenantId,
        accessToken: accessToken,
    }), createEvent = _f.createEvent, createEventError = _f.eventError, setCreateEventError = _f.setEventError, creatingEvent = _f.composingEvent;
    var fetchingSpace = useFetchInitialSpaceForComposer({
        mainSpaceId: initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.spaceId,
    }).fetchingSpace;
    var _g = __read(useState(undefined), 2), completedSpaceRequestFormId = _g[0], setCompletedSpaceRequestFormId = _g[1];
    var handleDateChange = useEventDateTime({
        timezone: timezone,
    }).handleDateChange;
    var _h = useCalendarIntegrationsContext(), calendarType = _h.calendarType, calendarMailboxAddress = _h.calendarMailboxAddress;
    var handleEventCreation = useHandleEventCreation({
        formData: formData,
        timezone: timezone,
        createEvent: createEvent,
        calendarType: calendarType,
        calendarMailboxAddress: calendarMailboxAddress,
        requestOnlySpace: requestOnlySpace,
        onSubmit: onSubmit,
        onClose: onClose,
        onTrackBufferEvent: onTrackBufferEvent,
    }).handleEventCreation;
    var _j = useSpacePrivacySettings({
        spaceSettings: ((_c = (_b = formData.spaces) === null || _b === void 0 ? void 0 : _b.find(function (space) { return space.id === Number(initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.spaceId); })) === null || _c === void 0 ? void 0 : _c.settings) || [],
    }), spaceReservationVisibilityEnabled = _j.spaceReservationVisibilityEnabled, shouldShowPrivacyIndicator = _j.shouldShowPrivacyIndicator;
    useEffect(function () {
        if (!onEventChange)
            return;
        var callback = subscribe({
            formState: {
                values: true,
            },
            callback: function (_a) {
                var values = _a.values;
                onEventChange(values);
            },
        });
        return function () { return callback(); };
    }, [subscribe, onEventChange]);
    /* UseEffect that updates the date and space if the event changes */
    useEffect(function () {
        var _a;
        if (!parentEvent)
            return;
        if (parentEvent.date && !((_a = formData.date) === null || _a === void 0 ? void 0 : _a.isSame(parentEvent.date))) {
            handleDateChange(parentEvent.date);
        }
    }, [formData.date, handleDateChange, parentEvent]);
    return (_jsx(CreateEventContext.Provider, { value: {
            initialEvent: initialEvent,
            fetchingSpace: fetchingSpace,
            handleEventCreation: handleEventCreation,
            createEventError: createEventError,
            creatingEvent: creatingEvent,
            setCreateEventError: setCreateEventError,
            completedSpaceRequestFormId: completedSpaceRequestFormId,
            setCompletedSpaceRequestFormId: setCompletedSpaceRequestFormId,
            shouldShowPrivacyIndicator: shouldShowPrivacyIndicator,
            spaceReservationVisibilityEnabled: spaceReservationVisibilityEnabled,
        }, children: children }));
};
export var useCreateEventContext = function () {
    return useContext(CreateEventContext);
};
